import React, { useMemo, useState } from 'react';
import { Avatar, Box, Typography, IconButton, InputBase } from '@mui/material';
import {
  West as WestIcon,
  Check as CheckIcon,
  Search as SearchIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { Button } from '../../../../Components/MUIs';
import BottomSheet from '../../../../Components/Ui/BottomSheet';
import Icon from '../../../../Assets/Icon/CustomIcon';
import { useDispatch, useSelector } from 'react-redux';
import { showSnackbar } from '../../../../Reducer/SnackbarReducer';
import { PermitActions } from '../../../../Reducer/PermitReducer';
import { BottomSheetActions } from '../../../../Reducer/BottomSheetReducer';

const getMemberDuty = (mem = {}) =>
  [mem.rank, mem.position].filter(Boolean).join(' · ');

const isExternalMember = (mem = {}) => Boolean(mem.companyNm);

const getMemberAffiliation = (mem = {}, dep = {}) =>
  mem.companyNm || dep.depNmLast || dep.depNm || '';

const formatMemberSubtitle = (mem = {}) => {
  if (isExternalMember(mem)) {
    return mem.companyNm || '-';
  }

  return '';
};

export default function SelectDepHeadIdBottomSheet({ Save1 }) {
  const dispatch = useDispatch();

  const { depApproval, approvalLine } = useSelector((state) => state.permit);
  const { depHeads, safetyManager, observer } = approvalLine;
  const bottomSheetState = useSelector((state) => state.bottomSheet);
  const { userInfo } = useSelector((state) => state.BaseInfo);

  // 부모로부터 받은 선택 타입 (INTERMEDIATE, FINAL, WITNESS)
  const selectionType = bottomSheetState.data?.selectionType || 'INTERMEDIATE';

  const [selectedDep, setSelectedDep] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const updateApprovalLine = (nextState) => {
    dispatch(
      PermitActions.setApprovalLine({
        ...nextState,
        // 최종결재자 ID 연동 유지
        smId: nextState.safetyManager?.smId ?? approvalLine.smId ?? '',
      })
    );
  };

  const handleBack = () => {
    setSelectedDep(null);
    setSearchQuery('');
  };

  const filteredManagers = useMemo(() => {
    if (!selectedDep) return [];
    const query = searchQuery.trim().toLowerCase();
    const myId = String(userInfo?.decUserId || '');

    return (selectedDep.managers || []).filter((m) => {
      const mId = String(m.id || '');

      // 입회자는 중복/자기자신 제한 없음
      if (selectionType === 'WITNESS') {
        return (
          !query ||
          m.name?.toLowerCase().includes(query) ||
          m.position?.toLowerCase().includes(query) ||
          m.rank?.toLowerCase().includes(query) ||
          m.companyNm?.toLowerCase().includes(query)
        );
      }

      // 자기 자신 제외 (중간결재자, 최종결재자 공통)
      if (myId && mId === myId) return false;

      // 중간결재자: 최종결재자로 선택된 사람 제외
      if (
        selectionType === 'INTERMEDIATE' &&
        safetyManager?.smId &&
        mId === String(safetyManager.smId)
      )
        return false;

      // 최종결재자: 중간결재자로 선택된 사람 제외
      if (
        selectionType === 'FINAL' &&
        depHeads.some((h) => String(h.headId) === mId)
      )
        return false;

      return (
        !query ||
        m.name?.toLowerCase().includes(query) ||
        m.position?.toLowerCase().includes(query) ||
        m.rank?.toLowerCase().includes(query) ||
        m.companyNm?.toLowerCase().includes(query)
      );
    });
  }, [selectedDep, searchQuery, selectionType, userInfo, depHeads, safetyManager]);

  const handleSelectManager = (dep, manager) => {
    const memberDuty = getMemberDuty(manager);
    const commonData = {
      depCd: dep.depCd,
      depNm: getMemberAffiliation(manager, dep),
      rank: manager.rank || '',
      jobPosition: manager.position || '',
      position: memberDuty,
      photo: manager.photo,
    };

    // ✅ 1. 중간결재자 (INTERMEDIATE)
    if (selectionType === 'INTERMEDIATE') {
      const isSelected = depHeads.some((h) => h.headId === manager.id);
      if (isSelected) {
        updateApprovalLine({
          depHeads: depHeads.filter((h) => h.headId !== manager.id),
        });
      } else {
        if (depHeads.length >= 4) {
          dispatch(
            showSnackbar({
              message: '최대 4명까지 선택 가능합니다.',
              type: 'warning',
            })
          );
          return;
        }
        updateApprovalLine({
          depHeads: [
            ...depHeads,
            { ...commonData, headId: manager.id, headNm: manager.name },
          ],
        });
      }
    }
    // ✅ 2. 최종결재자 (FINAL)
    else if (selectionType === 'FINAL') {
      const isSame = safetyManager?.smId === manager.id;
      updateApprovalLine({
        safetyManager: isSame
          ? null
          : { ...commonData, smId: manager.id, smNm: manager.name },
        smId: isSame ? '' : manager.id,
      });
    }
    // ✅ 3. 입회자 (WITNESS)
    else if (selectionType === 'WITNESS') {
      const isSame = observer?.id === manager.id;
      updateApprovalLine({
        observer: isSame
          ? null
          : { ...commonData, id: manager.id, name: manager.name },
      });
    }
  };

  const titleMap = {
    INTERMEDIATE: '중간결재자 선택',
    FINAL: '최종결재자 선택',
    WITNESS: '입회 담당자 선택',
  };

  return (
    <BottomSheet
      isHeaderChildren
      headerChildren={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {selectedDep && (
            <IconButton onClick={handleBack} sx={{ p: '6px' }}>
              <WestIcon sx={{ fontSize: '22px' }} />
            </IconButton>
          )}
          <Typography sx={{ fontSize: '16px', fontWeight: 600 }}>
            {selectedDep ? selectedDep.depNm : titleMap[selectionType]}
          </Typography>
        </Box>
      }
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '520px',
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        {selectedDep && (
          <Box
            sx={{
              mx: 2,
              my: 1.5,
              display: 'flex',
              alignItems: 'center',
              bgcolor: '#fafafa',
              borderRadius: '12px',
              px: 1.5,
              py: 1,
              border: '1px solid #eee',
            }}
          >
            <SearchIcon sx={{ fontSize: 18, color: '#aaa', mr: 1 }} />
            <InputBase
              placeholder="이름 또는 직급 검색"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ flex: 1, fontSize: '15px' }}
            />
            {searchQuery && (
              <IconButton size="small" onClick={() => setSearchQuery('')}>
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}
          </Box>
        )}

        <Box
          data-body-scroll-lock-ignore
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {selectedDep
            ? filteredManagers.map((mem) => {
                // ✅ 중요: 현재 열린 바텀시트 타입(selectionType)에 따라서만 체크 표시를 판단해야 함
                let isSelected = false;
                if (selectionType === 'INTERMEDIATE') {
                  isSelected = depHeads.some((h) => h.headId === mem.id);
                } else if (selectionType === 'FINAL') {
                  isSelected = safetyManager?.smId === mem.id;
                } else if (selectionType === 'WITNESS') {
                  isSelected = observer?.id === mem.id;
                }

                return (
                  <button
                    key={mem.id}
                    onClick={() => handleSelectManager(selectedDep, mem)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                      padding: '14px 20px',
                      border: 'none',
                      borderBottom: '1px solid #f5f5f5',
                      background: isSelected ? '#f6fbf5' : '#fff',
                      cursor: 'pointer',
                      textAlign: 'left',
                    }}
                  >
                    <Avatar
                      src={mem.photo}
                      sx={{ width: 42, height: 42, mr: 2 }}
                    >
                      {!mem.photo && <Icon.User />}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontSize: '15px', fontWeight: 600 }}>
                        {mem.name}
                        {getMemberDuty(mem) && (
                          <Box
                            component="span"
                            sx={{
                              ml: 0.75,
                              fontSize: '13px',
                              fontWeight: 500,
                              color: '#666',
                            }}
                          >
                            {getMemberDuty(mem)}
                          </Box>
                        )}
                      </Typography>
                      {formatMemberSubtitle(mem) && (
                        <Typography sx={{ fontSize: '13px', color: '#888' }}>
                          {formatMemberSubtitle(mem)}
                        </Typography>
                      )}
                    </Box>
                    {isSelected && <CheckIcon sx={{ color: '#22C55E' }} />}
                  </button>
                );
              })
            : depApproval.map((dep) => (
                <button
                  key={dep.depCd}
                  onClick={() => setSelectedDep(dep)}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    width: 'calc(100% - 32px)',
                    margin: '8px 16px',
                    padding: '16px',
                    borderRadius: '12px',
                    border: '1px solid #eee',
                    background: '#fff',
                  }}
                >
                  <Typography sx={{ fontSize: '15px', fontWeight: 600 }}>
                    {dep.depNmLast || dep.depNm}
                  </Typography>
                  <Typography sx={{ color: '#ccc' }}>›</Typography>
                </button>
              ))}
        </Box>

        <Box
          sx={{
            px: 2,
            py: 2.5,
            borderTop: '1px solid #f0f0f0',
            backgroundColor: '#fff',
          }}
        >
          <Button
            name="확인"
            fullWidth
            sx={{
              height: 52,
              borderRadius: '16px',
              backgroundColor: '#22C55E',
              color: '#fff',
              fontWeight: 'bold',
            }}
            onClick={() => dispatch(BottomSheetActions.CloseBottomSheet())}
          />
        </Box>
      </Box>
    </BottomSheet>
  );
}
